import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Page,
  FormLayout,
  TextField,
  Button,
  Banner,
  Text,
  Frame,
  Modal,
  Stack,
  Link
} from '@shopify/polaris';
import { useAuth } from '../../contexts/AuthContext';
import LandingNav from '../landing/components/LandingNav';

function AuthPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      setLoading(true);
      await login(email, password);
      // No need to navigate here, the AuthContext will handle that
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setResetError('');
    setResetSuccess(false);

    if (!resetEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }

    try {
      setResetLoading(true);
      await resetPassword(resetEmail);
      setResetSuccess(true);
    } catch (err) {
      setResetError(err.message || 'Failed to send password reset email');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Frame>
      <Page>
        <LandingNav currentPath="/auth" onNavClick={(path) => navigate(path)} />
        
        <div style={{
          background: 'linear-gradient(135deg, #f5f5f5 0%, #e0f7fa 100%)',
          minHeight: 'calc(100vh - 120px)',
          padding: '40px 0'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            padding: '0 20px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            <div style={{ width: '100%', maxWidth: '450px' }}>
              <Card>
                <Card.Section>
                  <Text variant="headingLg" as="h2" alignment="center">
                    Log in to your account
                  </Text>
                </Card.Section>

                {error && (
                  <Card.Section>
                    <Banner status="critical" onDismiss={() => setError('')}>
                      <p>{error}</p>
                    </Banner>
                  </Card.Section>
                )}

                <Card.Section>
                  <form onSubmit={handleSubmit}>
                    <FormLayout>
                      <TextField
                        type="email"
                        label="Email"
                        value={email}
                        onChange={setEmail}
                        autoComplete="email"
                        required
                        disabled={loading}
                      />
                      <TextField
                        type="password"
                        label="Password"
                        value={password}
                        onChange={setPassword}
                        autoComplete="current-password"
                        required
                        disabled={loading}
                      />
                      <Button
                        submit
                        primary
                        fullWidth
                        loading={loading}
                        disabled={loading}
                      >
                        Log in
                      </Button>
                    </FormLayout>
                  </form>
                </Card.Section>

                <Card.Section>
                  <Stack vertical spacing="extraTight">
                    <Text variant="bodyMd" as="p" alignment="center">
                      Don't have an account?{' '}
                      <a href="/register" style={{ textDecoration: 'underline', cursor: 'pointer', color: '#008060' }}>Register</a>
                    </Text>
                    <Text variant="bodyMd" as="p" alignment="center">
                      <Link onClick={() => setShowForgotPasswordModal(true)}>
                        Forgot password?
                      </Link>
                    </Text>
                  </Stack>
                </Card.Section>
              </Card>
              
              {/* Trust Indicators */}
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <Text variant="bodySm" color="subdued">
                  <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                    <span>🔒 Secure Login</span>
                    <span>|</span>
                    <span>🛡️ Data Protection</span>
                    <span>|</span>
                    <span>💳 Stripe Payments</span>
                  </span>
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Page>

      <Modal
        open={showForgotPasswordModal}
        onClose={() => {
          setShowForgotPasswordModal(false);
          setResetEmail('');
          setResetError('');
          setResetSuccess(false);
        }}
        title="Reset Password"
        primaryAction={{
          content: 'Send Reset Email',
          onAction: handleResetPassword,
          loading: resetLoading,
          disabled: resetLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowForgotPasswordModal(false);
              setResetEmail('');
              setResetError('');
              setResetSuccess(false);
            },
          },
        ]}
      >
        <Modal.Section>
          {resetError && (
            <Banner status="critical" onDismiss={() => setResetError('')}>
              <p>{resetError}</p>
            </Banner>
          )}

          {resetSuccess && (
            <Banner status="success">
              <p>Password reset email sent. Please check your inbox.</p>
            </Banner>
          )}

          <FormLayout>
            <TextField
              type="email"
              label="Email"
              value={resetEmail}
              onChange={setResetEmail}
              autoComplete="email"
              helpText="We'll send a password reset link to this email address."
              disabled={resetLoading}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Frame>
  );
}

export default AuthPage;
